
import { useState, useEffect, memo, useRef } from "react"
import "katex/dist/katex.min.css"
import katex from "katex"
import mermaid from "mermaid"
import { motion } from "framer-motion"
// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "Segoe UI, sans-serif",
})

const typewriterEffectResponse = async (content, setDisplayContent, useTypewriter = true) => {
  const isMounted = true

  // If typewriter effect is disabled, immediately set the full content
  if (!useTypewriter) {
    // Process the content in the same way but without delays
    content = content.replace(/\\\[\s*/g, "\\[")
    content = content.replace(/\s*\\\]/g, "\\]")
    content = content.replace(/\\\(\s*/g, "\\(")
    content = content.replace(/\\\\\\\(\s*/g, "\\(\\\\")
    content = content.replace(/\s*\\\)/g, "\\)")
    
    // Process headings before lists
    content = processHeadings(content)
    
    // Improved list handling for nested lists
    content = processStructuredContent(content)

    function transformSpecialText(input) {
      const pattern = /\*\*(.*?)\*\*/g

      function transformSegment(segment) {
        const parts = segment.split(/(\\$.*?\\$)/)
        return parts
          .map((part) => {
            if (/^\\$.*\\$/.test(part)) {
              return part
            }
            return `<span class="ai-bold">${part}</span><br>`
          })
          .join("")
      }

      const output = input.replace(pattern, (fullMatch, boldText) => {
        const transformed = transformSegment(boldText)
        return transformed
      })

      return output
    }

    content = transformSpecialText(content)
    content = content.replace(/\\n(?![a-zA-Z])/g, "$\\\\$")
    
    // Enhance horizontal lines (---)
    content = content.replace(/^---+$/gm, '<div class="ai-horizontal-divider"></div>')

    // Enhance tables
    content = content.replace(/\|(.+)\|\n\|([-:|\s]+)\|\n((?:\|.+\|\n)+)/gm, (match, header, separator, rows) => {
      const headerCells = header.split("|").map((cell) => cell.trim())
      const separatorCells = separator.split("|").map((cell) => cell.trim())
      const rowsArray = rows.trim().split("\n")

      let tableHTML = '<div class="ai-table-container"><table class="ai-table">'

      // Table header
      tableHTML += "<thead><tr>"
      headerCells.forEach((cell) => {
        if (cell) tableHTML += `<th>${cell}</th>`
      })
      tableHTML += "</tr></thead>"

      // Table body
      tableHTML += "<tbody>"
      rowsArray.forEach((row) => {
        tableHTML += "<tr>"
        const cells = row.split("|").map((cell) => cell.trim())
        cells.forEach((cell) => {
          if (cell !== "") tableHTML += `<td>${cell}</td>`
        })
        tableHTML += "</tr>"
      })

      tableHTML += "</tbody></table></div>"
      return tableHTML
    })

    // Process Mermaid diagrams
    content = content.replace(/\`\`\`mermaid\n([\s\S]*?)\`\`\`/g, (match, diagramCode) => {
      const id = `mermaid-diagram-${Math.random().toString(36).substring(2, 10)}`
      return `<div class="mermaid-container"><div class="mermaid" id="${id}">${diagramCode}</div></div>`
    })

    // Process LaTeX content all at once
    const regex =
      /(\\$[\s\S]*?\\$)|(\\$$[\s\S]*?\\$$)|(\\\[[\s\S]*?\\\])|(\\\([\s\S]*?\\\))|(\$\$[\s\S]*?\$\$)|(\$[\s\S]*?\$)|(\$[\s\S]*?\$)|(\\begin\{array\}[\s\S]*?\\end\{array\})|(\\textit\{.*?\})|(\\textbf\{.*?\})/g
    let displayContent = ""
    let lastIndex = 0
    let match

    while ((match = regex.exec(content)) !== null) {
      // Add text before the LaTeX
      if (match.index > lastIndex) {
        displayContent += content.substring(lastIndex, match.index).replace(/\n/g, "<br />")
      }

      const part = match[0]
      try {
        const isDisplayMode = part.startsWith("\\[") || part.startsWith("$$")
        const mathContent = part
          .replace(/\\\[|\\\]/g, "") // Remove \[ and \]
          .replace(/\\\(|\\\)/g, "") // Remove \( and \)
          .replace(/\\$$|\\$$/g, "") // Remove $$ and $$
          .replace(/\$\$/g, "") // Remove $$
          .replace(/\$/g, "") // Remove $
          .trim();
        const renderedMath = katex.renderToString(mathContent, { throwOnError: false, displayMode: isDisplayMode })
        displayContent += renderedMath
      } catch (e) {
        console.error("Error rendering LaTeX:", e)
        displayContent += `<span class="error">Failed to render LaTeX: ${part}</span>`
      }

      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < content.length) {
      displayContent += content.substring(lastIndex).replace(/\n/g, "<br />")
    }

    setDisplayContent(displayContent)
    return
  }

  // Original typewriter effect code for when useTypewriter is true
  content = content.replace(/\\\[\s*/g, "\\[") 
  content = content.replace(/\s*\\\]/g, "\\]") 
  content = content.replace(/\\\(\s*/g, "\\(") 
  content = content.replace(/\\\\\\\(\s*/g, "\\(\\\\") 
  content = content.replace(/\s*\\\)/g, "\\)") 

  // Process headings before lists
  content = processHeadings(content)
  
  // Process lists for typewriter mode
  content = processStructuredContent(content)

  function transformSpecialText(input) {
    const pattern = /\*\*(.*?)\*\*/g

    function transformSegment(segment) {
      const parts = segment.split(/(\\$.*?\\$)/)
      return parts
        .map((part) => {
          if (/^\\$.*\\$/.test(part)) {
            return part
          }
          return `<span class="ai-bold">${part}</span><br>`
        })
        .join("")
    }

    const output = input.replace(pattern, (fullMatch, boldText) => {
      const transformed = transformSegment(boldText)
      return transformed
    })

    return output
  }

  content = transformSpecialText(content)
  content = content.replace(/\\n(?![a-zA-Z])/g, "$\\\\$")

  // Enhance horizontal lines (---)
  content = content.replace(/^---+$/gm, '<div class="ai-horizontal-divider"></div>')

  // Enhance tables
  content = content.replace(/\|(.+)\|\n\|([-:|\s]+)\|\n((?:\|.+\|\n)+)/gm, (match, header, separator, rows) => {
    const headerCells = header.split("|").map((cell) => cell.trim())
    const separatorCells = separator.split("|").map((cell) => cell.trim())
    const rowsArray = rows.trim().split("\n")

    let tableHTML = '<div class="ai-table-container"><table class="ai-table">'

    // Table header
    tableHTML += "<thead><tr>"
    headerCells.forEach((cell) => {
      if (cell) tableHTML += `<th>${cell}</th>`
    })
    tableHTML += "</tr></thead>"

    // Table body
    tableHTML += "<tbody>"
    rowsArray.forEach((row) => {
      tableHTML += "<tr>"
      const cells = row.split("|").map((cell) => cell.trim())
      cells.forEach((cell) => {
        if (cell !== "") tableHTML += `<td>${cell}</td>`
      })
      tableHTML += "</tr>"
    })

    tableHTML += "</tbody></table></div>"
    return tableHTML
  })

  // Process Mermaid diagrams
  content = content.replace(/\`\`\`mermaid\n([\s\S]*?)\`\`\`/g, (match, diagramCode) => {
    const id = `mermaid-diagram-${Math.random().toString(36).substring(2, 10)}`
    return `<div class="mermaid-container"><div class="mermaid" id="${id}">${diagramCode}</div></div>`
  })

  const regex =
    /(\\$[\s\S]*?\\$)|(\\$$[\s\S]*?\\$$)|(\\\[[\s\S]*?\\\])|(\\\([\s\S]*?\\\))|(\$\$[\s\S]*?\$\$)|(\$[\s\S]*?\$)|(\\begin\{array\}[\s\S]*?\\end\{array\})|(\\textit\{.*?\})|(\\textbf\{.*?\})/g
  const parts = content.split(regex).filter((part) => part !== null && part !== undefined && part !== "")
  let displayContent = ""
  for (const part of parts) {
    if (
      part.startsWith("\\(") ||
      part.startsWith("\\[") ||
      part.startsWith("\$") ||
      part.startsWith("\$\$") ||
      part.startsWith("\\begin") ||
      part.startsWith("\\textit") ||
      part.startsWith("\\textbf")
    ) {
      // Process LaTeX content
      try {
        const isDisplayMode = part.startsWith("\\[") || part.startsWith("\$\$")
        const mathContent = part
          .replace(/\\\[|\\\]/g, "") // Remove \[ and \]
          .replace(/\\\(|\\\)/g, "") // Remove \( and \)
          .replace(/\\$$|\\$$/g, "") // Remove $$ and $$
          .replace(/\$\$/g, "") // Remove $$
          .replace(/\$/g, "") // Remove $
          .trim();
        const renderedMath = katex.renderToString(mathContent, { throwOnError: false, displayMode: isDisplayMode })

        // Simulate typing the LaTeX
        for (let i = 0; i < renderedMath.length; i++) {
          displayContent += renderedMath[i]

          if (isMounted) setDisplayContent(displayContent)
          /*                     await delayRandomTime();
           */
        }
      } catch (e) {
        console.error("Error rendering LaTeX:", e)
        displayContent += `<span class="error">Failed to render LaTeX: ${part}</span>`
        if (isMounted) setDisplayContent(displayContent)
      }
    } else {
      // Process plain text content
      for (let i = 0; i < part.length; i++) {
        displayContent += part[i] === "\n" ? "<br />" : part[i]
        if (isMounted) setDisplayContent(displayContent)
        await textdelayRandomTime()
      }
    }
  }
}

// Process headings (###, ##, #)
function processHeadings(content) {
  // Process headings (###, ##, #)
  return content.replace(/^(#{1,6})\s+(.+)$/gm, (match, hashes, text) => {
    const level = hashes.length;
    return `<span${level} class="ai-heading ai-heading-${level}">${text}</span${level}>`;
  });
}

// Process structured content based on indentation
function processStructuredContent(content) {
  const lines = content.split('\n');
  const processed = [];
  
  let itemCounter = 0;
  let inList = false;
  let previousIndent = 0;
  const INDENT_UNIT = 10;      // ← 2 spaces = one logical level
  const BASE_PADDING = 20;    // ← first level left-pad

  for (const raw of lines) {
    if (!raw.trim()) { processed.push(raw); continue; }
    
    const m = raw.match(/^(\s*)[-*•]\s+(.+)$/);
    if (!m) { inList = false; itemCounter = 0; processed.push(raw); continue; }
    
    const indent = m[1].length;
    const text = m[2];
    
    // -- open / reset counters ------------------------------------------
    if (!inList) { inList = true; itemCounter = 0; previousIndent = indent; }
    
    if (indent !== previousIndent) {
      if (indent > previousIndent) {
        // went DEEPER → fresh counter for sub-list
        itemCounter = 0;
      } else {
        // went SHALLOWER → treat as a brand-new list
        itemCounter = 0;
      }
      previousIndent = indent;
    }
    
    itemCounter++;
    
    // translate raw spaces into a clear visual step
    const level = Math.floor(indent / INDENT_UNIT);
    const padding = BASE_PADDING + level * BASE_PADDING;
    
    // Optional: Use bullet for top level, numbers for sub-levels
    const isTopLevel = level === 0;
    const marker = isTopLevel
       ? `<span class="ai-list-disc"></span>`
       : `<span class="ai-list-number">${itemCounter}</span>`;
    
    processed.push(
      `<div class="ai-list-item" style="padding-left:${padding}px">
        ${marker}
        ${processLatexInContent(text)}
      </div>`
    );
  }
  
  return processed.join('\n');
}

// Helper function to pre-process LaTeX in list content
function processLatexInContent(content) {
  // This function preserves LaTeX expressions in content
  return content.replace(/(\\$$.*?\\$$|\\\[.*?\\\]|\\\(.*?\\\)|\$.*?\$|\$\$.*?\$\$)/g, (match) => {
    return `{{LATEX_PLACEHOLDER_${encodeURIComponent(match)}}}`;
  });
}

const textdelayRandomTime = () => {
  return new Promise((resolve) => {
    const randomDelay = Math.floor(Math.random() * 5) + 1 // Delay between 5ms to 25ms
    setTimeout(resolve, randomDelay)
  })
}

// Main component to handle the content rendering
const TypewriterRendererResponsePlain = memo(({ content, useTypewriter = true }) => {
  const [displayContent, setDisplayContent] = useState("")
  const containerRef = useRef(null)

  useEffect(() => {
    let isMounted = true
    // Start the typewriter effect when the component loads

    setDisplayContent("") // Reset before running
    typewriterEffectResponse(content, setDisplayContent, useTypewriter)
    return () => {
      isMounted = false
    }
  }, [content, useTypewriter])

  // Initialize mermaid diagrams after content is rendered
  useEffect(() => {
    if (displayContent && containerRef.current) {
      // Use a small delay to ensure the content is fully rendered
      const timer = setTimeout(() => {
        const mermaidDiagrams = containerRef.current.querySelectorAll(".mermaid")
        if (mermaidDiagrams.length > 0) {
          console.log(`Found ${mermaidDiagrams.length} mermaid diagrams in TypeWriterResponsePlain`)

          try {
            // Try different approaches to render the diagrams

            // Approach 1: Process each diagram individually
            mermaidDiagrams.forEach((diagram, index) => {
              try {
                // Generate a unique ID if not present
                if (!diagram.id) {
                  diagram.id = `mermaid-diagram-${Date.now()}-${index}`
                }

                // Get the diagram code
                const code = diagram.textContent.trim()

                // Only process if there's actual content
                if (code) {
                  // Clear the element for rendering
                  diagram.textContent = ''

                  // Render the diagram
                  mermaid.render(diagram.id, code)
                    .then(result => {
                      diagram.innerHTML = result.svg
                    })
                    .catch(error => {
                      console.error(`Error rendering mermaid diagram ${index}:`, error)
                      // Restore the original code with syntax highlighting
                      diagram.innerHTML = `<pre class="mermaid-code">${code}</pre>`

                      // Try the init method as fallback
                      try {
                        mermaid.init(undefined, diagram)
                      } catch (initError) {
                        console.error(`Error initializing mermaid diagram ${index}:`, initError)
                      }
                    })
                }
              } catch (error) {
                console.error(`Error processing mermaid diagram ${index}:`, error)
              }
            })

            // Approach 2: Use contentLoaded as a fallback
            try {
              mermaid.contentLoaded()
            } catch (secondError) {
              console.error("Second mermaid initialization attempt failed:", secondError)
            }
          } catch (error) {
            console.error("Mermaid initialization error:", error)
          }
        }
      }, 300) // Small delay to ensure DOM is updated

      return () => clearTimeout(timer)
    }
  }, [displayContent])

  return (
    <div ref={containerRef}>
      <div
        style={{
          color: "#374151",
          fontFamily: "'Söhne', 'ui-sans-serif', 'system-ui', '-apple-system', 'Segoe UI', sans-serif",
          fontWeight: "500",
          fontSize: "18px",
          lineHeight: "1.3",
          letterSpacing: "-0.02em",
          maxHeight: "100%",
          boxSizing: "border-box",
          wordWrap: "break-word",
          textRendering: "optimizeLegibility",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
        dangerouslySetInnerHTML={{ __html: displayContent }}
      />

      <style jsx global>{`
  /* Base typography improvements */
  p {
    font-weight: 500 !important;
    margin: 1rem 0;
    line-height: 1.7;
    color: #374151;
  }

  /* Code and monospace improvements */
  code {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace;
    font-weight: 500 !important;

    font-size: 0.9em;
    background-color: #f3f4f6;
    padding: 0.2em 0.4em;
    border-radius: 4px;
    color: #1f2937;
  }

  pre {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace;
    font-weight: 500 !important;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    overflow-x: auto;
    line-height: 1.5;
    margin: 1.5rem 0;
  }

  /* Improved heading styles */
  .ai-heading {
    font-weight: 600;
    margin: 1.5rem 0 1rem 0;
    color: #111827;
    line-height: 1.3;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  .ai-heading-1 {
    font-size: 1.875rem;
    font-weight: 700;
    margin-top: 2rem;
  }

  .ai-heading-2 {
    font-size: 1.5rem;
    font-weight: 600;
  }

  .ai-heading-3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  .ai-heading-4 {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .ai-heading-5 {
    font-size: 1rem;
    font-weight: 600;
  }

  .ai-heading-6 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
  }

  /* Bold text improvements */
  .ai-bold, strong, b {
    font-weight: 600 !important;
    color: #111827;
  }

  /* List improvements */
  .ai-list-item {
    position: relative;
    margin-bottom: 0.75rem;
    line-height: 1.6;
    display: flex;
    align-items: flex-start;
    color: #374151;
  }

  .ai-list-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: #4f46e5;
    color: white;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    margin-right: 12px;
    text-align: center;
    flex-shrink: 0;
  }

  .ai-list-bullet {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: #4f46e5;
    color: white;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    margin-right: 12px;
    text-align: center;
    flex-shrink: 0;
  }

  .ai-list-disc {
    width: 0.375rem;
    height: 0.375rem;
    margin: 0.5rem 0.75rem 0 0;
    border-radius: 50%;
    background: #4f46e5;
    flex-shrink: 0;
  }

  /* Table improvements */
  .ai-table-container {
    overflow-x: auto;
    margin: 1.5rem 0;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
  }

  .ai-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
    border-radius: 12px;
    overflow: hidden;
    font-family: inherit;
  }

  .ai-table th {
    background-color: #f9fafb;
    color: #374151;
    font-weight: 600;
    text-align: left;
    padding: 12px 16px;
    border-bottom: 2px solid #e5e7eb;
    font-size: 0.875rem;
  }

  .ai-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
    line-height: 1.5;
  }

  .ai-table tbody tr:nth-child(even) {
    background-color: #fafafa;
  }

  .ai-table tbody tr:hover {
    background-color: #f3f4f6;
  }

  /* Blockquote styling */
  blockquote {
    border-left: 4px solid #4f46e5;
    margin: 1.5rem 0;
    padding: 1rem 1.5rem;
    background-color: #f8fafc;
    border-radius: 0 8px 8px 0;
    font-style: italic;
    color: #4b5563;
  }

  /* Link styling */
  a {
    color: #4f46e5;
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
  }

  a:hover {
    border-bottom-color: #4f46e5;
  }

  /* Math and code block improvements */
  .katex {
  font-weight: 600;
    font-size: 1.05em;
    margin: 0 0.1em;
  }

  .katex-display {
    margin: 0.5rem 0;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5em 0;
  }

  /* Mermaid improvements */
  .mermaid-container {
    background-color: #ffffff;
    padding: 1.5rem;
    border-radius: 12px;
    margin: 1.5rem 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e5e7eb;
    overflow-x: auto;
  }

  .mermaid {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
  }

  /* Horizontal divider - keeping your existing style */
  .ai-horizontal-divider {
    height: 3px;
    margin: 2rem 0;
    border: 0;
    background-image: radial-gradient(circle at 0% 50%, rgba(0, 0, 0, 0.4) 0%, transparent 30%),
                      radial-gradient(circle at 25% 50%, rgba(79, 70, 229, 0.8) 0%, transparent 30%),
                      radial-gradient(circle at 50% 50%, rgb(0, 0, 0) 0%, transparent 30%),
                      radial-gradient(circle at 75% 50%, rgba(79, 70, 229, 0.8) 0%, transparent 30%),
                      radial-gradient(circle at 100% 50%, rgba(0, 0, 0, 0.4) 0%, transparent 30%);
    background-size: 200% 100%;
    background-repeat: no-repeat;
    border-radius: 9999px;
    opacity: 0.9;
  }
`}</style>
    </div>
  )
})

export default TypewriterRendererResponsePlain
